import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { cashflowsApi, budgetsApi, settingsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus,
  ArrowLeft,
  Banknote,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
  MoreVertical,
  Trash2,
  Edit3,
  Eye,
  Archive,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Calendar,
  Target,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Building2,
  Copy,
  GitCompare,
  Layers
} from 'lucide-react';

const MONTHS = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
const MONTH_KEYS = ['jul', 'aug', 'sep', 'oct', 'nov', 'dec', 'jan', 'feb', 'mar', 'apr', 'may', 'jun'];

const SCENARIO_TYPES = [
  { value: 'base', label: 'Base Case', icon: Target, color: 'emerald' },
  { value: 'best_case', label: 'Best Case', icon: TrendingUp, color: 'blue' },
  { value: 'worst_case', label: 'Worst Case', icon: TrendingDown, color: 'amber' }
];

const CASHFLOW_CATEGORIES = [
  { value: 'inflow', label: 'Cash Inflows', icon: ArrowUpRight, color: 'emerald' },
  { value: 'outflow', label: 'Cash Outflows', icon: ArrowDownRight, color: 'red' }
];

const INFLOW_TYPES = [
  { value: 'sales', label: 'Sales/Collections' },
  { value: 'other_income', label: 'Other Income' },
  { value: 'loans', label: 'Loans/Financing' },
  { value: 'investments', label: 'Investments' },
  { value: 'asset_sales', label: 'Asset Sales' }
];

const OUTFLOW_TYPES = [
  { value: 'purchases', label: 'Purchases/COGS' },
  { value: 'salaries', label: 'Salaries & Wages' },
  { value: 'overhead', label: 'Operating Expenses' },
  { value: 'loan_repayment', label: 'Loan Repayments' },
  { value: 'capex', label: 'Capital Expenditure' },
  { value: 'taxes', label: 'Taxes' },
  { value: 'other', label: 'Other Payments' }
];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'R 0.00';
  const absValue = Math.abs(value);
  const formatted = new Intl.NumberFormat('en-ZA', { 
    style: 'currency', 
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(absValue);
  return value < 0 ? `(${formatted})` : formatted;
};

// Summary Card Component
const SummaryCard = ({ title, value, icon: Icon, trend, color, isDark, onClick, subtitle }) => (
  <Card 
    className={`border shadow-none rounded-sm cursor-pointer transition-all hover:shadow-md ${
      isDark ? 'bg-zinc-900 border-zinc-800 hover:border-zinc-700' : 'border-slate-200 hover:border-slate-300'
    }`}
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-xs font-medium ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>{title}</p>
          <p className={`text-2xl font-bold mt-1 ${
            color === 'emerald' ? 'text-emerald-600' : 
            color === 'red' ? 'text-red-600' : 
            color === 'amber' ? 'text-amber-600' :
            isDark ? 'text-white' : 'text-slate-900'
          }`}>
            {value}
          </p>
          {subtitle && (
            <p className={`text-xs mt-1 ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>{subtitle}</p>
          )}
        </div>
        <div className={`p-2 rounded-sm ${
          color === 'emerald' ? 'bg-emerald-100 text-emerald-600' :
          color === 'red' ? 'bg-red-100 text-red-600' :
          color === 'amber' ? 'bg-amber-100 text-amber-600' :
          isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-100 text-slate-600'
        }`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          <span>{trend >= 0 ? '+' : ''}{trend.toFixed(1)}% vs last month</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const CashflowPage = () => {
  const { user, isAdmin } = useAuth();
  const { isDark, setSelectedBu: setThemeBu } = useTheme();
  
  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cashflows, setCashflows] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [availableBudgets, setAvailableBudgets] = useState([]);
  
  // View state
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'detail'
  const [selectedCashflow, setSelectedCashflow] = useState(null);
  const [cashflowData, setCashflowData] = useState(null);
  const [cashflowSummary, setCashflowSummary] = useState(null);
  
  // Filters
  const [filterBuLocal, setFilterBuLocal] = useState('all');
  const [filterScenario, setFilterScenario] = useState('all');
  const [filterYear, setFilterYear] = useState('');
  
  // Wrapper to update theme context logo when BU changes
  const setFilterBu = (buId) => {
    setFilterBuLocal(buId);
    if (buId && buId !== 'all') {
      const bu = businessUnits.find(b => b.id === buId);
      if (setThemeBu) setThemeBu(buId, bu?.logo_url);
    } else if (setThemeBu) {
      setThemeBu('', null);
    }
  };
  const filterBu = filterBuLocal;
  
  // Create dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    fiscal_year: '2025/2026',
    business_unit_id: '',
    scenario_type: 'base',
    source_budget_id: '',
    description: '',
    opening_balance: 0,
    minimum_cash_threshold: 0
  });
  
  // Detail view state
  const [expandedCategories, setExpandedCategories] = useState(new Set(['inflow', 'outflow']));
  const [viewPeriod, setViewPeriod] = useState('monthly'); // 'monthly' or 'weekly'
  
  // What-If Comparison state
  const [compareDialog, setCompareDialog] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  // viewMode can be: 'list', 'detail', or 'compare'
  
  // Duplicate dialog
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, cashflow: null });
  const [duplicateData, setDuplicateData] = useState({ name: '', scenario_type: 'best_case' });
  
  // User permissions
  const userBuId = user?.business_unit_id;
  const canSeeAllBUs = isAdmin() || user?.is_cfo;
  
  // Load initial data
  useEffect(() => {
    loadData();
  }, []);
  
  // Reload when filters change
  useEffect(() => {
    if (!loading) {
      loadCashflows();
    }
  }, [filterBu, filterScenario, filterYear]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load business units
      const busRes = await settingsApi.getBusinessUnits();
      setBusinessUnits(busRes.data);
      
      // Load budgets for copying (approved, pending, draft)
      const budgetsRes = await budgetsApi.getAll();
      const copyable = budgetsRes.data.filter(b => ['approved', 'pending', 'draft'].includes(b.status));
      setAvailableBudgets(copyable);
      
      // Set default filter year
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      // Fiscal year starts in July, so if before July, we're in previous fiscal year
      const fiscalYear = currentMonth < 6 
        ? `${currentYear - 1}/${currentYear}` 
        : `${currentYear}/${currentYear + 1}`;
      setFilterYear(fiscalYear);
      
      await loadCashflows();
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadCashflows = async () => {
    try {
      setRefreshing(true);
      const params = {};
      
      if (!canSeeAllBUs && userBuId) {
        params.business_unit_id = userBuId;
      } else if (filterBu && filterBu !== 'all') {
        params.business_unit_id = filterBu;
      }
      
      if (filterScenario && filterScenario !== 'all') {
        params.scenario_type = filterScenario;
      }
      
      if (filterYear) {
        params.fiscal_year = filterYear;
      }
      
      const res = await cashflowsApi.getAll(params);
      setCashflows(res.data);
    } catch (error) {
      console.error('Failed to load cashflows:', error);
    } finally {
      setRefreshing(false);
    }
  };
  
  const loadCashflowDetail = async (cashflowId) => {
    try {
      const [detailRes, summaryRes] = await Promise.all([
        cashflowsApi.getById(cashflowId),
        cashflowsApi.getSummary(cashflowId)
      ]);
      setCashflowData(detailRes.data);
      setCashflowSummary(summaryRes.data);
      setSelectedCashflow(cashflowId);
      setViewMode('detail');
    } catch (error) {
      console.error('Failed to load cashflow details:', error);
      toast.error('Failed to load cashflow details');
    }
  };
  
  const handleCreateCashflow = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    if (!formData.business_unit_id) {
      toast.error('Please select a Business Unit');
      return;
    }
    
    try {
      setSubmitting(true);
      await cashflowsApi.create(formData);
      toast.success('Cashflow plan created');
      setCreateDialog(false);
      setFormData({
        name: '',
        fiscal_year: '2025/2026',
        business_unit_id: '',
        scenario_type: 'base',
        source_budget_id: '',
        description: '',
        opening_balance: 0,
        minimum_cash_threshold: 0
      });
      await loadCashflows();
    } catch (error) {
      console.error('Failed to create cashflow:', error);
      toast.error('Failed to create cashflow plan');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleDeleteCashflow = async (cashflowId) => {
    if (!window.confirm('Are you sure you want to delete this cashflow plan?')) return;
    
    try {
      await cashflowsApi.delete(cashflowId);
      toast.success('Cashflow plan deleted');
      if (selectedCashflow === cashflowId) {
        setViewMode('list');
        setSelectedCashflow(null);
        setCashflowData(null);
      }
      await loadCashflows();
    } catch (error) {
      console.error('Failed to delete cashflow:', error);
      toast.error('Failed to delete cashflow plan');
    }
  };
  
  const handleStatusChange = async (cashflowId, newStatus) => {
    try {
      await cashflowsApi.updateStatus(cashflowId, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      await loadCashflows();
      if (selectedCashflow === cashflowId) {
        await loadCashflowDetail(cashflowId);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };
  
  const goBackToList = () => {
    setViewMode('list');
    setSelectedCashflow(null);
    setCashflowData(null);
    setCashflowSummary(null);
    setComparisonData(null);
    setSelectedForCompare([]);
  };
  
  // What-If Comparison functions
  const toggleCompareSelection = (cashflowId) => {
    setSelectedForCompare(prev => {
      if (prev.includes(cashflowId)) {
        return prev.filter(id => id !== cashflowId);
      }
      if (prev.length >= 4) {
        toast.error('Maximum 4 scenarios can be compared');
        return prev;
      }
      return [...prev, cashflowId];
    });
  };
  
  const handleCompare = async () => {
    if (selectedForCompare.length < 2) {
      toast.error('Select at least 2 scenarios to compare');
      return;
    }
    
    try {
      const res = await cashflowsApi.compare(selectedForCompare);
      setComparisonData(res.data);
      setViewMode('compare');
      setCompareDialog(false);
    } catch (error) {
      console.error('Failed to compare scenarios:', error);
      toast.error('Failed to compare scenarios');
    }
  };
  
  const handleDuplicate = async () => {
    if (!duplicateDialog.cashflow || !duplicateData.name.trim()) {
      toast.error('Please enter a name for the new scenario');
      return;
    }
    
    try {
      setSubmitting(true);
      await cashflowsApi.duplicate(
        duplicateDialog.cashflow.id,
        duplicateData.scenario_type,
        duplicateData.name
      );
      toast.success(`Scenario duplicated as "${duplicateData.name}"`);
      setDuplicateDialog({ open: false, cashflow: null });
      setDuplicateData({ name: '', scenario_type: 'best_case' });
      await loadCashflows();
    } catch (error) {
      console.error('Failed to duplicate scenario:', error);
      toast.error('Failed to duplicate scenario');
    } finally {
      setSubmitting(false);
    }
  };
  
  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };
  
  // Calculate stats
  const stats = useMemo(() => ({
    total: cashflows.length,
    base: cashflows.filter(c => c.scenario_type === 'base').length,
    best: cashflows.filter(c => c.scenario_type === 'best_case').length,
    worst: cashflows.filter(c => c.scenario_type === 'worst_case').length,
    active: cashflows.filter(c => c.status === 'active').length,
    draft: cashflows.filter(c => c.status === 'draft').length
  }), [cashflows]);
  
  // Group line items by category and type
  const groupedItems = useMemo(() => {
    if (!cashflowData?.line_items) return { inflow: [], outflow: [] };
    
    const grouped = {
      inflow: {},
      outflow: {}
    };
    
    cashflowData.line_items.forEach(item => {
      const cat = item.category || 'outflow';
      const type = item.type || 'other';
      
      if (!grouped[cat][type]) {
        grouped[cat][type] = [];
      }
      grouped[cat][type].push(item);
    });
    
    return grouped;
  }, [cashflowData]);
  
  // Fiscal years
  const fiscalYears = ['2024/2025', '2025/2026', '2026/2027'];
  
  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="cashflow-loading">
        <Skeleton className="h-10 w-64 rounded-sm" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-sm" />)}
        </div>
        <Skeleton className="h-96 rounded-sm" />
      </div>
    );
  }
  
  // Detail View
  if (viewMode === 'detail' && cashflowData) {
    return (
      <div className="p-6 space-y-6" data-testid="cashflow-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={goBackToList} className="rounded-sm">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className={`text-2xl font-display font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {cashflowData.name}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <span className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {cashflowData.business_unit_name}
                </span>
                <span className={`text-sm ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>•</span>
                <span className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  FY {cashflowData.fiscal_year}
                </span>
                <span className={`px-2 py-0.5 text-xs rounded-sm font-medium ${
                  cashflowData.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                  cashflowData.status === 'draft' ? 'bg-slate-100 text-slate-700' :
                  'bg-zinc-100 text-zinc-700'
                }`}>
                  {cashflowData.status}
                </span>
                <span className={`px-2 py-0.5 text-xs rounded-sm font-medium ${
                  cashflowData.scenario_type === 'base' ? 'bg-emerald-100 text-emerald-700' :
                  cashflowData.scenario_type === 'best_case' ? 'bg-blue-100 text-blue-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {SCENARIO_TYPES.find(s => s.value === cashflowData.scenario_type)?.label || 'Base Case'}
                </span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Select value={viewPeriod} onValueChange={setViewPeriod}>
              <SelectTrigger className={`w-[140px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly View</SelectItem>
                <SelectItem value="weekly">Weekly View</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={() => loadCashflowDetail(selectedCashflow)} className="rounded-sm">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        
        {/* Summary Cards */}
        {cashflowSummary && (
          <div className="grid grid-cols-4 gap-4">
            <SummaryCard
              title="Opening Balance"
              value={formatCurrency(cashflowSummary.opening_balance)}
              icon={Banknote}
              color="default"
              isDark={isDark}
            />
            <SummaryCard
              title="Total Inflows"
              value={formatCurrency(cashflowSummary.total_inflows)}
              icon={ArrowUpRight}
              color="emerald"
              isDark={isDark}
            />
            <SummaryCard
              title="Total Outflows"
              value={formatCurrency(cashflowSummary.total_outflows)}
              icon={ArrowDownRight}
              color="red"
              isDark={isDark}
            />
            <SummaryCard
              title="Closing Balance"
              value={formatCurrency(cashflowSummary.closing_balance)}
              icon={Target}
              color={cashflowSummary.closing_balance >= cashflowSummary.minimum_threshold ? 'emerald' : 'amber'}
              isDark={isDark}
              subtitle={cashflowSummary.months_below_threshold > 0 
                ? `${cashflowSummary.months_below_threshold} months below threshold`
                : 'Above threshold all year'}
            />
          </div>
        )}
        
        {/* Monthly Cash Flow Table */}
        {viewPeriod === 'monthly' && cashflowSummary && (
          <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
            <CardHeader className={`py-3 border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
              <CardTitle className="font-display text-base font-medium flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-blue-600" />
                Monthly Cash Position
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                    <th className={`py-2 px-4 text-left font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Month</th>
                    <th className={`py-2 px-4 text-right font-medium text-emerald-600`}>Inflows</th>
                    <th className={`py-2 px-4 text-right font-medium text-red-600`}>Outflows</th>
                    <th className={`py-2 px-4 text-right font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Net Flow</th>
                    <th className={`py-2 px-4 text-right font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Closing Balance</th>
                    <th className={`py-2 px-4 text-center font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {cashflowSummary.monthly_summary.map((month, idx) => (
                    <tr key={month.month} className={`border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                      <td className={`py-2 px-4 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                        {MONTHS[idx]}
                      </td>
                      <td className="py-2 px-4 text-right font-mono text-emerald-600">
                        {formatCurrency(month.inflows)}
                      </td>
                      <td className="py-2 px-4 text-right font-mono text-red-600">
                        ({formatCurrency(month.outflows)})
                      </td>
                      <td className={`py-2 px-4 text-right font-mono ${month.net_flow >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {month.net_flow >= 0 ? '+' : ''}{formatCurrency(month.net_flow)}
                      </td>
                      <td className={`py-2 px-4 text-right font-mono font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {formatCurrency(month.closing_balance)}
                      </td>
                      <td className="py-2 px-4 text-center">
                        {month.below_threshold ? (
                          <span className="inline-flex items-center gap-1 text-amber-600">
                            <AlertTriangle className="w-4 h-4" />
                            <span className="text-xs">Below threshold</span>
                          </span>
                        ) : (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
        
        {/* Line Items by Category */}
        <div className="space-y-4">
          {CASHFLOW_CATEGORIES.map(category => {
            const Icon = category.icon;
            const items = groupedItems[category.value] || {};
            const typeList = category.value === 'inflow' ? INFLOW_TYPES : OUTFLOW_TYPES;
            const isExpanded = expandedCategories.has(category.value);
            
            return (
              <Card key={category.value} className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                <CardHeader 
                  className={`py-3 border-b cursor-pointer ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}
                  onClick={() => toggleCategory(category.value)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-display text-base font-medium flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <Icon className={`w-4 h-4 ${category.color === 'emerald' ? 'text-emerald-600' : 'text-red-600'}`} />
                      {category.label}
                    </CardTitle>
                  </div>
                </CardHeader>
                
                {isExpanded && (
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                          <th className={`py-2 px-4 text-left font-medium min-w-[200px] ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Item</th>
                          {MONTHS.map(month => (
                            <th key={month} className={`py-2 px-2 text-right font-medium min-w-[80px] ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                              {month}
                            </th>
                          ))}
                          <th className={`py-2 px-4 text-right font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {typeList.map(type => {
                          const typeItems = items[type.value] || [];
                          if (typeItems.length === 0) return null;
                          
                          return typeItems.map((item, idx) => {
                            const total = MONTH_KEYS.reduce((sum, m) => sum + (item[m] || 0), 0);
                            return (
                              <tr key={item.id} className={`border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                                <td className={`py-2 px-4 ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                  <div className="flex flex-col">
                                    <span className="font-medium">{item.name}</span>
                                    <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>{type.label}</span>
                                  </div>
                                </td>
                                {MONTH_KEYS.map(month => (
                                  <td key={month} className={`py-2 px-2 text-right font-mono text-xs ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
                                    {formatCurrency(item[month] || 0)}
                                  </td>
                                ))}
                                <td className={`py-2 px-4 text-right font-mono font-medium ${
                                  category.value === 'inflow' ? 'text-emerald-600' : 'text-red-600'
                                }`}>
                                  {formatCurrency(total)}
                                </td>
                              </tr>
                            );
                          });
                        })}
                      </tbody>
                    </table>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    );
  }
  
  // Comparison View
  if (viewMode === 'compare' && comparisonData) {
    return (
      <div className="p-6 space-y-6" data-testid="cashflow-compare">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={goBackToList} className="rounded-sm">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className={`text-2xl font-display font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Scenario Comparison
              </h1>
              <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                Comparing {comparisonData.scenarios.length} cashflow scenarios
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={goBackToList} className="rounded-sm">
            Close Comparison
          </Button>
        </div>
        
        {/* Summary Comparison Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
          {comparisonData.scenarios.map((scenario, idx) => (
            <Card key={scenario.id} className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'} ${idx === 0 ? 'ring-2 ring-emerald-500' : ''}`}>
              <CardHeader className={`py-3 border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                <CardTitle className="font-display text-sm font-medium flex items-center gap-2">
                  {idx === 0 && <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">Baseline</span>}
                  <span className={`px-2 py-0.5 text-xs rounded-sm font-medium ${
                    scenario.scenario_type === 'base' ? 'bg-emerald-100 text-emerald-700' :
                    scenario.scenario_type === 'best_case' ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {SCENARIO_TYPES.find(s => s.value === scenario.scenario_type)?.label || 'Base Case'}
                  </span>
                </CardTitle>
                <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'} truncate`}>{scenario.name}</p>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Opening</span>
                  <span className={`font-mono text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {formatCurrency(scenario.opening_balance)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-xs text-emerald-600`}>Inflows</span>
                  <span className="font-mono text-sm text-emerald-600">
                    +{formatCurrency(scenario.total_inflows)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-xs text-red-600`}>Outflows</span>
                  <span className="font-mono text-sm text-red-600">
                    -{formatCurrency(scenario.total_outflows)}
                  </span>
                </div>
                <div className={`pt-2 border-t ${isDark ? 'border-zinc-700' : 'border-slate-100'} flex justify-between items-center`}>
                  <span className={`text-xs font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Closing</span>
                  <span className={`font-mono text-lg font-bold ${
                    scenario.closing_balance >= 0 ? 'text-emerald-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(scenario.closing_balance)}
                  </span>
                </div>
                {scenario.variance_vs_baseline && (
                  <div className={`pt-2 border-t ${isDark ? 'border-zinc-700' : 'border-slate-100'}`}>
                    <span className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>vs Baseline</span>
                    <span className={`block font-mono text-sm font-medium ${
                      scenario.variance_vs_baseline.closing_balance >= 0 ? 'text-emerald-600' : 'text-red-600'
                    }`}>
                      {scenario.variance_vs_baseline.closing_balance >= 0 ? '+' : ''}{formatCurrency(scenario.variance_vs_baseline.closing_balance)}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* Monthly Comparison Table */}
        <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
          <CardHeader className={`py-3 border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
            <CardTitle className="font-display text-base font-medium flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-600" />
              Monthly Closing Balance Comparison
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                  <th className={`py-2 px-4 text-left font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>Month</th>
                  {comparisonData.scenarios.map((scenario, idx) => (
                    <th key={scenario.id} className={`py-2 px-4 text-right font-medium ${
                      scenario.scenario_type === 'base' ? 'text-emerald-600' :
                      scenario.scenario_type === 'best_case' ? 'text-blue-600' :
                      'text-amber-600'
                    }`}>
                      {idx === 0 ? '★ ' : ''}{SCENARIO_TYPES.find(s => s.value === scenario.scenario_type)?.label?.split(' ')[0] || 'Base'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MONTHS.map((month, idx) => (
                  <tr key={month} className={`border-t ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                    <td className={`py-2 px-4 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                      {month}
                    </td>
                    {comparisonData.scenarios.map(scenario => {
                      const monthData = scenario.monthly_data[idx];
                      return (
                        <td key={scenario.id} className={`py-2 px-4 text-right font-mono ${
                          monthData?.closing_balance >= 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(monthData?.closing_balance || 0)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // List View
  return (
    <div className="p-6 space-y-6" data-testid="cashflow-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-display font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Cashflow Planning
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
            Plan and monitor cash position across business units
          </p>
        </div>
        <div className="flex items-center gap-2">
          {cashflows.length >= 2 && (
            <Button 
              variant="outline" 
              onClick={() => setCompareDialog(true)} 
              className="rounded-sm"
              data-testid="compare-scenarios-btn"
            >
              <GitCompare className="w-4 h-4 mr-2" />
              Compare Scenarios
            </Button>
          )}
          <Button onClick={() => setCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="create-cashflow-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Cashflow Plan
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <SummaryCard
          title="Total Plans"
          value={stats.total}
          icon={Banknote}
          isDark={isDark}
        />
        <SummaryCard
          title="Base Case"
          value={stats.base}
          icon={Target}
          color="emerald"
          isDark={isDark}
        />
        <SummaryCard
          title="Best Case"
          value={stats.best}
          icon={TrendingUp}
          color="blue"
          isDark={isDark}
        />
        <SummaryCard
          title="Worst Case"
          value={stats.worst}
          icon={TrendingDown}
          color="amber"
          isDark={isDark}
        />
      </div>
      
      {/* Filters */}
      <div className="flex items-center gap-4">
        {canSeeAllBUs && (
          <Select value={filterBu} onValueChange={setFilterBu}>
            <SelectTrigger className={`w-[180px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
              <Building2 className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="All Business Units" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Business Units</SelectItem>
              {businessUnits.map(bu => (
                <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        <Select value={filterYear} onValueChange={setFilterYear}>
          <SelectTrigger className={`w-[140px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
            <Calendar className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue placeholder="Fiscal Year" />
          </SelectTrigger>
          <SelectContent>
            {fiscalYears.map(fy => (
              <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filterScenario} onValueChange={setFilterScenario}>
          <SelectTrigger className={`w-[140px] rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
            <SelectValue placeholder="All Scenarios" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Scenarios</SelectItem>
            {SCENARIO_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Button variant="outline" onClick={loadCashflows} disabled={refreshing} className="rounded-sm">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      
      {/* Cashflow List */}
      <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
        <CardHeader className={`py-3 border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
          <CardTitle className="font-display text-base font-medium">
            Cashflow Plans ({cashflows.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {cashflows.length === 0 ? (
            <div className="py-12 text-center">
              <Banknote className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-zinc-700' : 'text-slate-300'}`} />
              <p className={isDark ? 'text-zinc-400' : 'text-slate-500'}>No cashflow plans found</p>
              <p className={`text-sm mt-1 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                Create a new plan to start tracking cash position
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-zinc-800">
              {cashflows.map(cashflow => {
                const ScenarioIcon = SCENARIO_TYPES.find(s => s.value === cashflow.scenario_type)?.icon || Target;
                const scenarioColor = SCENARIO_TYPES.find(s => s.value === cashflow.scenario_type)?.color || 'emerald';
                
                return (
                  <div 
                    key={cashflow.id}
                    className={`p-4 flex items-center justify-between hover:${isDark ? 'bg-zinc-800/50' : 'bg-slate-50'} cursor-pointer transition-colors`}
                    onClick={() => loadCashflowDetail(cashflow.id)}
                    data-testid={`cashflow-row-${cashflow.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-sm ${
                        scenarioColor === 'emerald' ? 'bg-emerald-100 text-emerald-600' :
                        scenarioColor === 'blue' ? 'bg-blue-100 text-blue-600' :
                        'bg-amber-100 text-amber-600'
                      }`}>
                        <ScenarioIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {cashflow.name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                            {cashflow.business_unit_name}
                          </span>
                          <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>•</span>
                          <span className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                            FY {cashflow.fiscal_year}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-0.5 text-xs rounded-sm font-medium ${
                        cashflow.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                        cashflow.status === 'draft' ? 'bg-slate-100 text-slate-700' :
                        'bg-zinc-100 text-zinc-700'
                      }`}>
                        {cashflow.status}
                      </span>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="rounded-sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); loadCashflowDetail(cashflow.id); }}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { 
                            e.stopPropagation(); 
                            setDuplicateData({ name: `${cashflow.name} - Copy`, scenario_type: 'best_case' });
                            setDuplicateDialog({ open: true, cashflow });
                          }}>
                            <Copy className="w-4 h-4 mr-2" />
                            Create What-If Scenario
                          </DropdownMenuItem>
                          {cashflow.status === 'draft' && (
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusChange(cashflow.id, 'active'); }}>
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Activate
                            </DropdownMenuItem>
                          )}
                          {cashflow.status === 'active' && (
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleStatusChange(cashflow.id, 'archived'); }}>
                              <Archive className="w-4 h-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); handleDeleteCashflow(cashflow.id); }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className={`sm:max-w-[500px] ${isDark ? 'bg-zinc-900 border-zinc-800' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-600" />
              Create Cashflow Plan
            </DialogTitle>
            <DialogDescription>
              Create a new cashflow plan. You can copy from an existing budget or start from scratch.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className={isDark ? 'text-zinc-300' : ''}>Plan Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Q1 2026 Base Case Cashflow"
                className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={isDark ? 'text-zinc-300' : ''}>Business Unit *</Label>
                <Select 
                  value={formData.business_unit_id} 
                  onValueChange={(v) => setFormData({ ...formData, business_unit_id: v })}
                >
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
                    <SelectValue placeholder="Select BU" />
                  </SelectTrigger>
                  <SelectContent>
                    {businessUnits.map(bu => (
                      <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className={isDark ? 'text-zinc-300' : ''}>Fiscal Year *</Label>
                <Select 
                  value={formData.fiscal_year} 
                  onValueChange={(v) => setFormData({ ...formData, fiscal_year: v })}
                >
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {fiscalYears.map(fy => (
                      <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className={isDark ? 'text-zinc-300' : ''}>Scenario Type</Label>
              <Select 
                value={formData.scenario_type} 
                onValueChange={(v) => setFormData({ ...formData, scenario_type: v })}
              >
                <SelectTrigger className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCENARIO_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className={isDark ? 'text-zinc-300' : ''}>Copy from Budget (Optional)</Label>
              <Select 
                value={formData.source_budget_id || 'none'} 
                onValueChange={(v) => setFormData({ ...formData, source_budget_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
                  <SelectValue placeholder="Start from scratch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Start from scratch</SelectItem>
                  {availableBudgets.map(budget => (
                    <SelectItem key={budget.id} value={budget.id}>
                      {budget.name} - {budget.status === 'approved' ? '✓' : budget.status === 'pending' ? '⏳' : '📝'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={isDark ? 'text-zinc-300' : ''}>Opening Balance</Label>
                <Input
                  type="number"
                  value={formData.opening_balance}
                  onChange={(e) => setFormData({ ...formData, opening_balance: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                  className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
                />
              </div>
              
              <div>
                <Label className={isDark ? 'text-zinc-300' : ''}>Minimum Cash Threshold</Label>
                <Input
                  type="number"
                  value={formData.minimum_cash_threshold}
                  onChange={(e) => setFormData({ ...formData, minimum_cash_threshold: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                  className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
                />
              </div>
            </div>
            
            <div>
              <Label className={isDark ? 'text-zinc-300' : ''}>Description</Label>
              <Input
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setCreateDialog(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCashflow} 
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-sm"
            >
              {submitting ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Creating...</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" />Create Plan</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Compare Scenarios Dialog */}
      <Dialog open={compareDialog} onOpenChange={setCompareDialog}>
        <DialogContent className={`sm:max-w-[500px] ${isDark ? 'bg-zinc-900 border-zinc-800' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-blue-600" />
              Compare Scenarios
            </DialogTitle>
            <DialogDescription>
              Select 2-4 cashflow scenarios to compare side by side.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4 max-h-[400px] overflow-y-auto">
            {cashflows.map(cashflow => {
              const ScenarioIcon = SCENARIO_TYPES.find(s => s.value === cashflow.scenario_type)?.icon || Target;
              const isSelected = selectedForCompare.includes(cashflow.id);
              
              return (
                <div 
                  key={cashflow.id}
                  className={`p-3 rounded-sm border cursor-pointer transition-all ${
                    isSelected 
                      ? isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'
                      : isDark ? 'border-zinc-700 hover:border-zinc-600' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => toggleCompareSelection(cashflow.id)}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox checked={isSelected} onChange={() => {}} />
                    <ScenarioIcon className={`w-4 h-4 ${
                      cashflow.scenario_type === 'base' ? 'text-emerald-600' :
                      cashflow.scenario_type === 'best_case' ? 'text-blue-600' :
                      'text-amber-600'
                    }`} />
                    <div className="flex-1">
                      <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {cashflow.name}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {cashflow.business_unit_name} • FY {cashflow.fiscal_year}
                      </p>
                    </div>
                    <span className={`px-2 py-0.5 text-xs rounded-sm font-medium ${
                      cashflow.scenario_type === 'base' ? 'bg-emerald-100 text-emerald-700' :
                      cashflow.scenario_type === 'best_case' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {SCENARIO_TYPES.find(s => s.value === cashflow.scenario_type)?.label || 'Base'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="flex justify-between items-center mt-4">
            <span className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
              {selectedForCompare.length} selected
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setCompareDialog(false); setSelectedForCompare([]); }} className="rounded-sm">
                Cancel
              </Button>
              <Button 
                onClick={handleCompare} 
                disabled={selectedForCompare.length < 2}
                className="bg-blue-600 hover:bg-blue-700 rounded-sm"
              >
                <GitCompare className="w-4 h-4 mr-2" />
                Compare ({selectedForCompare.length})
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Duplicate/What-If Dialog */}
      <Dialog open={duplicateDialog.open} onOpenChange={(open) => setDuplicateDialog({ open, cashflow: open ? duplicateDialog.cashflow : null })}>
        <DialogContent className={`sm:max-w-[400px] ${isDark ? 'bg-zinc-900 border-zinc-800' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-violet-600" />
              Create What-If Scenario
            </DialogTitle>
            <DialogDescription>
              Duplicate this cashflow plan with a different scenario type for comparison analysis.
            </DialogDescription>
          </DialogHeader>
          
          {duplicateDialog.cashflow && (
            <div className="space-y-4 mt-4">
              <div className={`p-3 rounded-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                <p className={`text-xs ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Source Plan</p>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{duplicateDialog.cashflow.name}</p>
              </div>
              
              <div>
                <Label className={isDark ? 'text-zinc-300' : ''}>New Scenario Name *</Label>
                <Input
                  value={duplicateData.name}
                  onChange={(e) => setDuplicateData({ ...duplicateData, name: e.target.value })}
                  placeholder="e.g., Base Case - Optimistic Revenue"
                  className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
                />
              </div>
              
              <div>
                <Label className={isDark ? 'text-zinc-300' : ''}>Scenario Type</Label>
                <Select 
                  value={duplicateData.scenario_type} 
                  onValueChange={(v) => setDuplicateData({ ...duplicateData, scenario_type: v })}
                >
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCENARIO_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setDuplicateDialog({ open: false, cashflow: null })} className="rounded-sm">
              Cancel
            </Button>
            <Button 
              onClick={handleDuplicate} 
              disabled={submitting || !duplicateData.name.trim()}
              className="bg-violet-600 hover:bg-violet-700 rounded-sm"
            >
              {submitting ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Creating...</>
              ) : (
                <><Copy className="w-4 h-4 mr-2" />Create Scenario</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashflowPage;
